import React from 'react';

const PrivacyPolicy = () => {
  const lastUpdated = 'January 15, 2025';

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Page header */}
      <div className="border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-em to-teal flex items-center justify-center text-white text-xs font-bold">
              NT
            </div>
            <span className="font-semibold text-slate-800 hidden sm:block">Nova Terra</span>
          </div>
          <a href="/" className="text-sm text-em-dark hover:text-em-deeper font-medium transition-colors">
            ← Back to site
          </a>
        </div>
        <div className="h-px gradient-em" />
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero block */}
        <div className="mb-12 space-y-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-em-light border border-em-border text-xs text-em-deeper font-semibold">
            Legal
          </span>
          <h1 className="text-4xl font-bold text-slate-900 leading-tight">Privacy Policy</h1>
          <p className="text-slate-400 text-sm">Last updated: {lastUpdated}</p>
          <p className="text-slate-500 leading-relaxed max-w-2xl">
            Nova Terra Consulting (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have.
          </p>
        </div>

        <div className="relative bg-white rounded-3xl border border-slate-100 shadow-card p-8 md:p-10 space-y-10 overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-0.5 gradient-em" />

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Information We Collect</h2>
            <ul className="space-y-2.5">
              {[
                'Contact details you provide (name, email, company, phone, and any details you share in messages or forms).',
                'Business context you choose to share so we can scope services.',
                'Usage data such as IP address, browser type, device identifiers, and pages visited (collected via cookies or similar technologies).',
                'Communications with us, including emails and meeting notes related to our engagement.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-em flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">How We Use Information</h2>
            <ul className="space-y-2.5">
              {[
                'Respond and provide services you request, including assessments, proposals, and project delivery.',
                'Operate, secure, and improve our website and services.',
                'Communicate about updates, service information, and important notices.',
                'Meet legal, compliance, and contractual requirements.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-em flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Sharing Information</h2>
            <ul className="space-y-2.5">
              {[
                'With trusted service providers who support our operations (hosting, analytics, communications) under appropriate confidentiality and security commitments.',
                'When required by law, regulation, or legal process, or to protect rights, safety, and security.',
                'In connection with a business transaction (e.g., merger, acquisition) where data remains protected by this Policy or a successor policy with comparable safeguards.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-em flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Cookies and Analytics</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We may use cookies and similar technologies to understand site performance and improve your experience. You can adjust browser settings to refuse cookies or alert you when they are used; some site features may not function correctly without them.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Data Security and Retention</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We implement administrative, technical, and physical safeguards to protect information. No method is 100% secure, but we continuously work to protect data. We retain information only as long as needed for the purposes described above or to meet legal and contractual requirements.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Your Choices</h2>
            <ul className="space-y-2.5">
              {[
                'You can opt out of marketing communications by using the unsubscribe instructions in our messages or by contacting us directly.',
                'You can disable cookies in your browser settings. Essential cookies may still be needed for core site functionality.',
                'You may request access, correction, or deletion of your personal information where applicable by law.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-em flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">International Use</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              If you access our site from outside the United States, you consent to processing and storage of your information in the United States and other locations where we or our providers operate, subject to this Policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Contact Us</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              For questions or requests about this Policy or your information, contact us at{' '}
              <a href="mailto:info@novaterraconsulting.net" className="text-em-dark hover:text-em-deeper font-medium transition-colors">
                info@novaterraconsulting.net
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <a href="/" className="text-sm text-em-dark hover:text-em-deeper font-medium transition-colors">
            ← Back to Nova Terra Consulting
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
